import React, { Fragment, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { TechpackItem as TechpackItemType } from "../../types";
import { palette } from "../../styles/theme";
import { useTranslation } from "react-i18next";
import { ADMIN_TOOLBAR_HEIGHT } from "../../containers/Admin/AdminToolbar";
import { EditableTextInput } from "../../common/components/Input/EditableTextInput";

interface Props {
  items: TechpackItemType[];
  containerId: string;
  projectNumber?: string;
  customerNumber?: string;
  onProjectNumberChange?: (value: string) => void;
  onCustomerChange?: (value: string) => void;
}

const TechpackItemNavigator = ({
  items,
  containerId,
  projectNumber,
  customerNumber,
  onProjectNumberChange,
  onCustomerChange,
}: Props) => {
  const { t } = useTranslation();

  // Internal state when no external id is provided
  const [internalActiveId, setInternalActiveId] = useState<string | undefined>(
    undefined
  );

  const findScrollableElement = (element: HTMLElement): HTMLElement | null => {
    // Check if this element has the data-overlayscrollbars-viewport attribute
    const viewport = element.querySelector("[data-overlayscrollbars-viewport]");
    if (viewport instanceof HTMLElement) {
      return viewport;
    }
    // Fallback: look for elements with overflow scroll/auto
    const scrollableElements = element.querySelectorAll("*");
    for (const el of scrollableElements) {
      const style = window.getComputedStyle(el);
      if (
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll"
      ) {
        return el as HTMLElement;
      }
    }
    return null;
  };

  const handleItemClick = (id: string) => {
    const element = document.getElementById(id);
    const containerElement = document.getElementById(containerId);
    if (element && containerElement) {
      const scrollableContainer = findScrollableElement(containerElement);
      if (scrollableContainer) {
        scrollableContainer.scrollTo({
          top:
            element.offsetTop -
            scrollableContainer.offsetTop -
            ADMIN_TOOLBAR_HEIGHT,
          behavior: "smooth",
        });
      } else {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  useEffect(() => {
    const containerElement = document.getElementById(containerId);
    if (!containerElement) return;

    const scrollableContainer = findScrollableElement(containerElement);
    if (!scrollableContainer) return;

    const getActiveItemId = () => {
      const containerRect = scrollableContainer.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerBottom = containerRect.bottom;

      let maxVisibleArea = 0;
      let activeItemId = undefined;

      const ordered = [...items].sort((a, b) => {
        // First sort by factory
        if (a.factory !== b.factory) {
          return a.factory.localeCompare(b.factory);
        }
        // Then sort by factory position
        return a.factoryPosition - b.factoryPosition;
      });

      for (const item of ordered) {
        const el = document.getElementById(`techpack-item-${item.id}`);
        if (!el) continue;

        const rect = el.getBoundingClientRect();

        // Calculate the visible area of this element within the container
        const visibleTop = Math.max(rect.top, containerTop);
        const visibleBottom = Math.min(rect.bottom, containerBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        if (visibleHeight > maxVisibleArea) {
          maxVisibleArea = visibleHeight;
          activeItemId = item.id;
        }
      }

      // If nothing is visible, return the first item
      if (!activeItemId && items.length > 0) {
        return items[0].id;
      }

      return activeItemId;
    };

    const handleScroll = () => {
      const id = getActiveItemId();
      setInternalActiveId(id);
    };

    // Initial determination
    handleScroll();
    scrollableContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      scrollableContainer.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId, items]);

  return (
    <Box
      sx={{
        width: "240px",
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        transition: "transform 0.3s ease-in-out",
        position: "sticky",
        top: "86px",
        minWidth: "100px",
      }}
    >
      <Box>
        <Typography variant="textLargeBold" mb={"4px"}>
          {t("techpack.title")}
        </Typography>
        {onProjectNumberChange && (
          <EditableTextInput
            name="projectNumber"
            defaultValue={projectNumber || ""}
            onConfirm={(value) => onProjectNumberChange(value)}
            label={t("techpack.project_number")}
            size="extraSmall"
            useModal={true}
            modalTitle={t("techpack.project_number")}
          />
        )}
        {onCustomerChange && (
          <EditableTextInput
            name="customerNumber"
            defaultValue={customerNumber || ""}
            onConfirm={(value) => onCustomerChange(value)}
            label={t("techpack.customer_number")}
            size="extraSmall"
            useModal={true}
            modalTitle={t("techpack.customer_number")}
          />
        )}
      </Box>
      <Box display={"flex"} flexDirection={"column"} gap={"12px"}>
        <Typography variant="textLargeBold">
          {t("techpack.jump_to_product")}
        </Typography>
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {items
            .sort((a, b) => {
              // First sort by factory
              if (a.factory !== b.factory) {
                return a.factory.localeCompare(b.factory);
              }
              // Then sort by factory position
              return a.factoryPosition - b.factoryPosition;
            })
            .map((item, index) => (
              <Fragment key={item.id}>
                <Box
                  sx={{
                    borderLeft: "2px solid",
                    borderColor: palette.grey.darkGrey,
                    width: "100%",
                    height: "12px",
                  }}
                />
                <Box
                  component="li"
                  onClick={() => handleItemClick(`techpack-item-${item.id}`)}
                  sx={(theme) => {
                    const isActive = internalActiveId === item.id;
                    return {
                      position: "relative",
                      paddingLeft: "16px",
                      cursor: "pointer",
                      borderLeft: "2px solid",
                      borderColor: isActive
                        ? palette.brand.secondary
                        : palette.grey.darkGrey,
                      color: isActive
                        ? palette.brand.secondary
                        : palette.grey.primary,
                      "&:hover": {
                        borderColor: palette.brand.secondary,
                        color: palette.brand.secondary,
                      },
                    };
                  }}
                >
                  <Typography variant="textRegular" sx={{ color: "inherit" }}>
                    {item.purchaseddesign.originalDesignName} (
                    {item.factoryInfo})
                  </Typography>
                </Box>
                {index === items.length - 1 && (
                  <Box
                    sx={{
                      borderLeft: "2px solid",
                      borderColor: palette.grey.darkGrey,
                      width: "100%",
                      height: "12px",
                    }}
                  />
                )}
              </Fragment>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TechpackItemNavigator;
